import React, { useEffect, useState } from "react";

import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useDebounce } from "use-debounce";
import shallow from "zustand/shallow";

import { Permission, useHoyerAcl } from "../../../../../hooks/useHoyerAcl";
import { IconPredefined } from "../../../../shared/icons/Icon";
import { PredefinedIconType } from "../../../../shared/icons/Icon/Predefined";
import LoadingIndicator from "../../../../shared/LoadingIndicator";
import useSnackbar from "../../../../shared/Snackbar/store";
import { iCardUser } from "../../DynPin/api";
import Pagination from "../../DynPin/components/Pagination";
import useDynPinStore from "../../DynPin/store";
import useDashboardStore from "../store";
import Search from "./Search";

export const Table = styled.table`
    display: block;
    ${({ theme }) =>
        theme.breakpoints.from.small.css`
            display: table;
            width: 100%;
            margin-top: 10px;
            color: ${theme.colors.blue.toString()};
            white-space: nowrap;
            border-collapse: collapse;
    `}
`;

export const THead = styled.thead`
    ${({ theme }) =>
        theme.breakpoints.upTo.extraSmall.css`
          display: none;
    `}
`;

export const TBody = styled.tbody`
    display: block;
    ${({ theme }) =>
        theme.breakpoints.from.small.css`
            display: table-row-group;
    `}
`;

export const Th = styled.th`
    display: block;
    ${({ theme }) =>
        theme.breakpoints.from.small.css`
            display: table-cell;
            padding: 12px 8px;
            color: ${theme.colors.blue.toString()};
    `}
`;

export const ActionTd = styled.td`
    display: flex;
    justify-content: end;

    ${({ theme }) =>
        theme.breakpoints.from.small.css`
           display: table-cell;
    `}
`;

export const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 39px;
`;

export const Tr = styled.tr`
    cursor: pointer;
    ${({ theme }) =>
        theme.breakpoints.upTo.extraSmall.css`
           display: block;
            padding: 18px;
            margin: 10px 0;

            :nth-of-type(even) {
                background-color: ${theme.colors.grey.toString()};
            }
    `}

    ${({ theme }) =>
        theme.breakpoints.from.small.css`
            display: table-row;
            border: 1px solid ${theme.colors.darkGrey.toString()};
    `}
`;

export const Td = styled.td`
    ${({ theme }) =>
        theme.breakpoints.upTo.extraSmall.css`
           display: flex;
           flex-wrap: wrap;
           gap: 5px;
           align-items: center;
           padding-top: 8px;
           padding-bottom: 8px;

           :nth-of-type(1) {
               ::before {
                   font-weight: normal;
                   content: "Kartennummer:";
               }
           }

           :nth-of-type(2) {
               ::before {
                   font-weight: normal;
                   content: "Karteninhaber:";
               }
           }

            :nth-of-type(3) {
               ::before {
                   font-weight: normal;
                   content: "E-Mail-Adresse:";
               }
           }
    `}

    ${({ theme }) =>
        theme.breakpoints.from.small.css`
            display: table-cell;
            padding: 8px;
            border: 1px solid ${theme.colors.darkGrey.toString()};
    `}
`;

const CardUser = ({ ...props }) => {
    const { can } = useHoyerAcl();
    const theme = useTheme();
    const { notify, confirm } = useSnackbar();
    const [selectedCustomerNumber] = useDashboardStore(
        (state) => [state.selectedCustomerNumber],
        shallow
    );
    const [loading, setLoading] = useState<boolean>(false);
    const [
        cards,
        getCardsUser,
        search,
        detachUser,
        page,
        meta,
        links,
        setPage,
    ] = useDynPinStore(
        (state) => [
            state.cards,
            state.getCardsUser,
            state.search,
            state.detachUser,
            state.page,
            state.meta,
            state.links,
            state.setPage,
        ],
        shallow
    );

    const [debouncedSearch] = useDebounce(search, 500);

    useEffect(() => {
        if (selectedCustomerNumber && can(Permission.CARD_DYN_PIN)) {
            getCardsUser(selectedCustomerNumber, debouncedSearch, page);
        }
    }, [selectedCustomerNumber, debouncedSearch, page]);

    const handleClick = (card: iCardUser) => {
        confirm(
            "Zuordnung aufheben",
            `Ohne eine Zuordnung kann die dynamische PIN nicht mehr verwendet werden. Die Karte kann danach von jedem genutzt werden, der die alte PIN kennt. Möchtest Du die Zuordnung der Karte ${card.card_number} zu ${card.user_name} aufheben?`
        ).then((confirmed) => {
            if (confirmed && selectedCustomerNumber) {
                setLoading(true);
                detachUser(
                    card,
                    (response) => {
                        notify("Erfolgreich", response.message);
                        getCardsUser(
                            selectedCustomerNumber,
                            debouncedSearch,
                            1
                        );
                        setLoading(false);
                    },
                    (error) => {
                        notify("Error", error.message, "error");
                        setLoading(false);
                    }
                );
            }
        });
    };
    return (
        selectedCustomerNumber &&
        cards && (
            <>
                <Table>
                    <THead>
                        <tr>
                            <Th>Kartennummer</Th>
                            <Th>Karteninhaber</Th>
                            <Th>Rolle</Th>
                            <Th>E-Mail-Adresse</Th>
                            <Th></Th>
                        </tr>
                    </THead>
                    <TBody>
                        {cards && cards.length > 0 ? (
                            cards.map((card) => (
                                <Tr
                                    key={`${card.card_number}-${card.user_email}`}
                                >
                                    <Td>{card.card_number}</Td>
                                    <Td>{card.user_name}</Td>
                                    <Td>
                                        {card.display_name
                                            ? card.display_name
                                                  .split(",")
                                                  .map((r) => r.trim())
                                                  .join(", ")
                                            : "–"}
                                    </Td>
                                    <Td>{card.user_email}</Td>
                                    <ActionTd>
                                        <IconContainer>
                                            {!loading ? (
                                                <IconPredefined
                                                    type={
                                                        PredefinedIconType.dustbin
                                                    }
                                                    cursor="pointer"
                                                    size="24px"
                                                    sExtraSmall="24px"
                                                    color={theme.colors.blue.toString()}
                                                    hoverColor={theme.colors.red.toString()}
                                                    onClick={() =>
                                                        handleClick(card)
                                                    }
                                                />
                                            ) : (
                                                <LoadingIndicator />
                                            )}
                                        </IconContainer>
                                    </ActionTd>
                                </Tr>
                            ))
                        ) : (
                            <Tr>
                                <Td colSpan={4} style={{ textAlign: "center" }}>
                                    Es gibt noch keine Karteninhaber.
                                </Td>
                            </Tr>
                        )}
                    </TBody>
                </Table>
                <Pagination
                    paginationLinks={links}
                    paginationMeta={meta}
                    setPage={setPage}
                />
            </>
        )
    );
};

export default CardUser;

export const CardsUserSearch = () => {
    const [setSearch, setPage] = useDynPinStore(
        (state) => [state.setSearch, state.setPage],
        shallow
    );

    return (
        <Search
            onSearch={(search) => {
                setSearch(search);
                setPage(1);
            }}
        />
    );
};
